import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnterprise, EnterpriseProvider } from '../../contexts/EnterpriseContext';
import { Badge } from '../../components/ui/badge';
import { Toaster, toast } from 'sonner';
import {
  Zap, ChevronDown, AlertTriangle, FileText, Sparkles, CheckCircle2, XCircle, Edit3
} from 'lucide-react';

const DENIAL_TAG_COLORS = {
  'Missing modifier': { bg: '#fffbeb', color: '#d97706' },
  'Medical necessity': { bg: '#fef2f2', color: '#ef4444' },
  'Authorization': { bg: '#fff7ed', color: '#f97316' },
  'Duplicate': { bg: '#f5f5f5', color: '#888' },
  'Timely filing': { bg: '#fef2f2', color: '#ef4444' },
};

const DenialsView = () => {
  const navigate = useNavigate();
  const enterprise = useEnterprise();
  const currentRole = enterprise?.currentRole || 'denials';
  const deniedClaims = enterprise?.deniedClaims || [];
  const [activeId, setActiveId] = useState(deniedClaims[0]?.id);
  const [queueFilter, setQueueFilter] = useState('open');
  const [editingAppeal, setEditingAppeal] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);

  const activeClaim = deniedClaims.find(c => c.id === activeId) || deniedClaims[0];
  if (!activeClaim) return null;

  const openCount = deniedClaims.length;
  const totalAtRisk = deniedClaims.reduce((s, c) => s + c.amount, 0);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column', background: '#fff' }}
      data-testid="denials-view">
      {/* Top bar */}
      <div style={{ height: 48, background: '#111', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12, flexShrink: 0 }}>
        <button onClick={() => navigate('/revq')} style={{ color: '#fff', opacity: 0.5, fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          <ChevronDown style={{ width: 10, height: 10, transform: 'rotate(90deg)' }} /> revQ
        </button>
        <Badge variant="outline" style={{ fontSize: 10, border: '1px solid rgba(107,92,231,0.4)', color: 'rgba(107,92,231,0.8)' }}>
          <Zap style={{ width: 10, height: 10 }} /> Billing Focus
        </Badge>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowRoleSwitcher(prev => !prev)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'rgba(255,255,255,.55)', background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
            data-testid="denials-role-switcher">
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: enterprise?.roles?.[currentRole]?.color || '#ef4444' }} />
            <span>{enterprise?.roles?.[currentRole]?.label || 'Denials Specialist'}</span>
            <ChevronDown style={{ width: 10, height: 10, opacity: 0.5 }} />
          </button>
          {showRoleSwitcher && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 60 }} onClick={() => setShowRoleSwitcher(false)} />
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, width: 220, background: '#1a1a1a', border: '1px solid rgba(255,255,255,.12)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.4)', zIndex: 61, overflow: 'hidden' }}
                data-testid="denials-role-dropdown">
                <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,.3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Switch role view</span>
                </div>
                {Object.entries(enterprise?.roles || {}).map(([key, r]) => (
                  <button key={key}
                    onClick={() => { setShowRoleSwitcher(false); enterprise?.setRole(key);
                      if (key === 'supervisor') navigate('/revq/supervisor');
                      else if (key === 'coder') navigate('/revq/coder');
                      else if (key === 'denials') navigate('/revq/denials-focus');
                      else navigate('/revq/billing-focus');
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', border: 'none', cursor: 'pointer', textAlign: 'left', background: currentRole === key ? 'rgba(255,255,255,.08)' : 'transparent' }}
                    data-testid={`denials-switch-${key}`}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 12, fontWeight: currentRole === key ? 500 : 400, color: currentRole === key ? '#fff' : 'rgba(255,255,255,.6)', margin: 0 }}>{r.label}</p>
                    </div>
                    {currentRole === key && <CheckCircle2 style={{ width: 11, height: 11, color: '#22c55e', flexShrink: 0 }} />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={() => navigate('/revq')} style={{ color: 'rgba(255,255,255,.5)', fontSize: 11, background: 'none', border: 'none', cursor: 'pointer' }}>
            &larr; Return
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <div style={{ width: 220, borderRight: '0.5px solid #e8e8e8', display: 'flex', flexDirection: 'column', background: '#fafafa', flexShrink: 0 }}>
          <div style={{ padding: '9px 14px', borderBottom: '0.5px solid #f0f0f0' }}>
            <div className="flex items-center gap-1.5" style={{ background: '#f0f0f0', borderRadius: 5, padding: '3px 8px', width: 'fit-content' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444' }} />
              <span style={{ fontSize: 11, color: '#555' }}>Billing Focus mode &middot; Denials</span>
            </div>
          </div>

          {/* Queue tabs */}
          <div className="flex" style={{ borderBottom: '0.5px solid #f0f0f0' }}>
            {['open', 'progress', 'resolved'].map(t => (
              <button key={t} onClick={() => setQueueFilter(t)}
                style={{ flex: 1, fontSize: 10, padding: '7px 4px', color: queueFilter === t ? '#111' : '#aaa', fontWeight: queueFilter === t ? 500 : 400, borderBottom: `2px solid ${queueFilter === t ? '#ef4444' : 'transparent'}`, background: 'none', border: 'none', borderBottomWidth: 2, borderBottomStyle: 'solid', borderBottomColor: queueFilter === t ? '#ef4444' : 'transparent', cursor: 'pointer' }}>
                {t === 'open' ? `Open (${openCount})` : t === 'progress' ? 'In progress (0)' : 'Resolved (0)'}
              </button>
            ))}
          </div>

          {/* Denial summary strip */}
          <div style={{ padding: '8px 14px', borderBottom: '0.5px solid #f0f0f0', background: '#fef2f2' }}>
            <p style={{ fontSize: 11, fontWeight: 500, color: '#ef4444', margin: 0 }}>${totalAtRisk.toLocaleString()} in open denials</p>
            <p style={{ fontSize: 10, color: '#888', marginTop: 2 }}>Avg. days to resolve: 12 days</p>
          </div>

          {/* Denied claims list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '6px 6px' }}>
            {deniedClaims.map(c => (
              <button key={c.id} onClick={() => setActiveId(c.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 6, marginBottom: 2,
                  background: activeId === c.id ? '#fff' : 'transparent', border: activeId === c.id ? '0.5px solid #e0e0e0' : '0.5px solid transparent',
                  cursor: 'pointer',
                }}
                data-testid={`denial-row-${c.id}`}>
                <div className="flex items-center justify-between">
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>{c.patient}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>${c.amount}</span>
                </div>
                <p style={{ fontSize: 10, color: '#aaa', marginTop: 1 }}>{c.provider} &middot; {c.dos}</p>
                <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
                  <span style={{
                    fontSize: 9, padding: '1px 6px', borderRadius: 10, fontWeight: 500,
                    background: DENIAL_TAG_COLORS[c.denialReason]?.bg || '#f5f5f5',
                    color: DENIAL_TAG_COLORS[c.denialReason]?.color || '#888',
                  }}>{c.denialReason}</span>
                  <span style={{ fontSize: 9, color: '#aaa' }}>{c.daysSince}d ago</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
          {/* Claim header */}
          <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
            <div className="flex items-center gap-3">
              <div style={{ width: 40, height: 40, borderRadius: 8, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <XCircle style={{ width: 18, height: 18, color: '#ef4444' }} />
              </div>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 600, color: '#111', margin: 0 }}>{activeClaim.patient}
                  <span style={{ fontSize: 12, color: '#aaa', fontWeight: 400, marginLeft: 8 }}>{activeClaim.id}</span>
                </h2>
                <p style={{ fontSize: 12, color: '#888', marginTop: 1 }}>{activeClaim.provider} &middot; {activeClaim.dos} &middot; {activeClaim.payer}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {correcting ? (
                <button onClick={() => { setCorrecting(false); toast.success('Corrections saved — ready to resubmit'); }}
                  style={{ height: 34, padding: '0 14px', background: '#22c55e', color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer' }}
                  data-testid="save-corrections-btn">
                  Mark corrected &mdash; ready to resubmit
                </button>
              ) : (
                <button onClick={() => setCorrecting(true)}
                  style={{ height: 34, padding: '0 14px', background: '#fff', color: '#555', borderRadius: 7, fontSize: 12, border: '0.5px solid #e0e0e0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                  data-testid="make-corrections-btn">
                  <Edit3 style={{ width: 12, height: 12 }} /> Make corrections
                </button>
              )}
              <button style={{ height: 34, padding: '0 14px', background: '#6b5ce7', color: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer' }}
                onClick={() => toast.success('Claim resubmitted')}
                data-testid="resubmit-btn">
                Resubmit &rarr;
              </button>
            </div>
          </div>

          {/* Denial Detail Panel */}
          <div style={{ background: '#fef2f2', border: '0.5px solid #fca5a5', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}
            data-testid="denial-detail-panel">
            <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
              <XCircle style={{ width: 14, height: 14, color: '#ef4444' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>Denial details</span>
              <span style={{ fontSize: 11, color: '#888' }}>{activeClaim.payer} &middot; Denied {activeClaim.denialDate}</span>
            </div>

            {/* CARC code with plain English */}
            <div style={{ marginBottom: 10 }}>
              <code style={{ fontSize: 12, fontWeight: 600, color: '#111', fontFamily: 'monospace' }}>{activeClaim.carc}</code>
              <p style={{ fontSize: 12, color: '#555', marginTop: 4, lineHeight: 1.6 }}>{activeClaim.carcPlain}</p>
            </div>

            {/* Denial reason category */}
            <span style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 500,
              background: DENIAL_TAG_COLORS[activeClaim.denialReason]?.bg || '#f5f5f5',
              color: DENIAL_TAG_COLORS[activeClaim.denialReason]?.color || '#888',
            }}>{activeClaim.reasonCategory}</span>

            {/* Resolution path */}
            <div style={{ background: '#f5f3ff', borderRadius: 8, padding: '10px 12px', marginTop: 12 }}>
              <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                <Sparkles style={{ width: 12, height: 12, color: '#6b5ce7' }} />
                <span style={{ fontSize: 11, fontWeight: 500, color: '#6b5ce7' }}>Suggested resolution</span>
              </div>
              <ol style={{ paddingLeft: 16, margin: 0 }}>
                {(activeClaim.resolution || []).map((step, i) => (
                  <li key={i} style={{ fontSize: 11, color: '#555', lineHeight: 1.8 }}>{step}</li>
                ))}
              </ol>
            </div>
          </div>

          {/* Appeal Letter */}
          {activeClaim.appealLetter && (
            <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}
              data-testid="appeal-letter-panel">
              <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText style={{ width: 14, height: 14, color: '#555' }} />
                <span style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>Appeal letter</span>
                <Badge style={{ fontSize: 9, background: '#f5f3ff', color: '#6b5ce7', border: 'none' }}>AI Draft</Badge>
              </div>
              <div style={{ padding: '12px 16px', maxHeight: 200, overflowY: 'auto' }}>
                <pre style={{ fontSize: 11, color: '#555', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                  {activeClaim.appealLetter}
                </pre>
              </div>
              <div style={{ padding: '10px 16px', borderTop: '0.5px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10, color: '#aaa' }}>{activeClaim.appealLetter.length} characters &middot; ~1 page</span>
                <div className="flex gap-2">
                  <button onClick={() => setEditingAppeal(!editingAppeal)}
                    style={{ height: 30, padding: '0 12px', border: '0.5px solid #e0e0e0', borderRadius: 6, fontSize: 11, color: '#555', background: '#fff', cursor: 'pointer' }}>
                    Edit letter
                  </button>
                  <button onClick={() => toast.success('Appeal sent')}
                    style={{ height: 30, padding: '0 12px', background: '#6b5ce7', color: '#fff', borderRadius: 6, fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer' }}
                    data-testid="send-appeal-btn">
                    Send appeal
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Service lines (read-only or correction mode) */}
          <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, padding: '14px 16px' }}
            data-testid="denial-service-lines">
            <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>Service line items</span>
              {correcting && <span style={{ fontSize: 10, color: '#d97706' }}>Editing mode &middot; changes will be flagged</span>}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                  {['CPT', 'Description', 'Charge', 'Modifiers'].map(h => (
                    <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(activeClaim.lines || []).map((line, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid #f5f5f5' }}>
                    <td style={{ padding: '8px 10px' }}>
                      <code style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>{line.cpt}</code>
                    </td>
                    <td style={{ padding: '8px 10px', fontSize: 12, color: '#555' }}>
                      {activeClaim.dx?.find(d => d.code)?.desc || '—'}
                    </td>
                    <td style={{ padding: '8px 10px', fontSize: 12, color: '#111' }}>${line.charge}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <div className="flex gap-1">
                        {(line.mod || ['', '', '', '']).map((m, mi) => (
                          <input key={mi} value={m} readOnly={!correcting}
                            onChange={() => {}}
                            style={{
                              width: 28, height: 22, textAlign: 'center', fontSize: 10,
                              border: `0.5px solid ${correcting ? '#6b5ce7' : '#e0e0e0'}`, borderRadius: 4,
                              background: correcting ? '#f5f3ff' : '#f9f9f9', color: '#555',
                            }} />
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Toaster position="bottom-right" />
    </div>
  );
};

const DenialsViewWrapped = () => (
  <EnterpriseProvider>
    <DenialsView />
  </EnterpriseProvider>
);

export default DenialsViewWrapped;
